import FormPage from '@/components/FormPage'
import { createGetParams, createPostParams } from '@/rest'
import {
  formatPhoneNumber,
  getRm,
  getNumeralFromRm,
  stringGen,
} from '@/utils'

const formTitle = 'Prepaid topup'

export default {
  extends: FormPage,
  data () {
    return {
      name: null,
      street1: null,
      street2: null,
      city: null,
      postalCode: null,
      state: null,
      breadcrumbs: [
        {
          text: 'Dealer Kits', disabled: false, to: '/dealer_kits',
        },
        {
          text: 'Prepaid', disabled: false, to: '/prepaid_kits',
        },
        {
          text: formTitle, disabled: true,
        },
      ],
      title: formTitle,
      // Form steps
      topupDetails: {
        fields: {
          ecash: {
            name: 'E-recharge balance',
          },
          msisdn: {
            component: () => import(/* webpackChunkName: "msisdnField" */ '@/components/MsisdnField'),
            name: 'Mobile number',
            props: {
              existing: true,
              noBlack: true,
              hint: 'Prepaid mobile number',
              label: 'Enter mobile number',
              presistentHint: true,
              required: true,
              rules: [val => !!val || 'Prepaid mobile number required!'],
              searchNetwork: true,
            },
          },
          topup: {
            component: () => import(/* webpackChunkName: "amountField" */ '@/components/AmountField'),
            name: 'Topup',
            props: {
              label: 'Enter topup amount',
              required: true,
            },
          },
        },
        title: 'Topup details',
      },
      submission: {
        fields: {
          msisdn: {
            name: 'Mobile number',
          },
          topup: {
            emphasize: true,
            name: 'Topup',
          },
        },
        submit: false,
        errorMessage: null,
        status: null,
        subTitle: 'Check before submit',
        title: 'Submit',
      },
    }
  },
  computed: {
    formSteps: function () {
      return {
        topupDetails: this.topupDetails,
        submission: this.submission,
      }
    },
    ecash: function () {
      return this.topupDetails.fields.ecash
    },
    topup: function () {
      return this.topupDetails.fields.topup.value
    },
  },
  mounted: function () {
    this.topupDetails.fields.topup.props.rules.push(
      () => this.validateTopup(),
    )
    const params = createGetParams({
      erecharge: true,
    })
    this.$rest.get('getDealerResources.php', params).then(function (response) {
      this.ecash.error = null
      this.ecash.value = getRm(response.data.erecharge)
    }.bind(this)).catch(error => {
      this.ecash.error = error.message
    })
  },
  watch: {
    'topupDetails.fields.msisdn.value': function (val) {
      this.submission.fields.msisdn.value = formatPhoneNumber(val)
      if (!val) return

      if ((val.length === 11 && val.startsWith('011')) || (val.length === 10 && !val.startsWith('011'))) {
        const params = createGetParams({
          target: this.topupDetails.fields.msisdn.value,
        })
        this.$rest.get('getProfileFromNetwork.php', params)
          .then(response => {
            this.name = response.data.name
            this.street1 = response.data.street1 ? response.data.street1 : null
            this.street2 = response.data.street2 ? response.data.street2 : null
            this.city = response.data.city ? response.data.city : null
            this.postalCode = response.data.postalCode ? response.data.postalCode : null
            this.state = response.data.state ? response.data.state : null
          }).catch(() => {
            // ignore
          })
      }
    },
    'topupDetails.fields.topup.value': function (val) {
      this.submission.fields.topup.value = getRm(val)

      if (Number(val) > getNumeralFromRm(this.ecash.value)) {
        this.topupDetails.fields.topup.validate && this.topupDetails.fields.topup.validate(true)
      } else {
        this.submission.fields.topup.value = getRm(val)
        this.topupDetails.fields.topup.validate && this.topupDetails.fields.topup.validate()
      }
    },
  },
  methods: {
    validateTopup: function () {
      if (Number(this.topup) <= 0) return 'Topup amount required!'
      if (Number(this.topup) > getNumeralFromRm(this.ecash.value)) return 'Insufficient e-recharge!'
      return true
    },
    // Override
    async submit () {
      const params = createPostParams({
        action: 'topup',
        payload: {
          target: this.topupDetails.fields.msisdn.value,
          amount: Number(this.topupDetails.fields.topup.value).toFixed(2),
        },
      })

      try {
        const response = await this.$rest.post('postAction.php', params)
        this.$nextTick(() => {
          this.alertText = 'Your request to make a Prepaid topup was successfully submitted.'
          this.status = this.submission.status = 'success'
          this.submission.submit = false
          this.isImpersonated() && this.showSubmissionResponse(response.data)
          // this.downloadReceipt()
        })
      } catch (e) {
        this.$nextTick(() => {
          this.status = this.submission.status = 'error'
          this.submission.errorMessage = e.message + ': ' + e.response.data
          this.submission.submit = false
        })
      }
    },
    async downloadReceipt () {
      let invoice = 'P-' + stringGen(9)
      invoice = invoice.toUpperCase()

      const payload = {
        msisdn: this.topupDetails.fields.msisdn.value,
        amount: this.topupDetails.fields.topup.value,
        invoice: invoice,
        name: this.name,
        street1: this.street1,
        street2: this.street2,
        city: this.city,
        postalCode: this.postalCode,
        state: this.state,
      }

      const data = process.env.NODE_ENV === 'production' ? { payload: payload }
        : { impersonate: 223992, payload: payload }
      /* eslint-disable no-empty */
      try {
        const response = await this.$rest({
          url: process.env.NODE_ENV === 'production' ? location.protocol + '//' + location.hostname + '/api/pdf/topupReceipt.php'
            : 'http://onesys.onexox.my/pdf/topupReceipt.php',
          method: 'POST',
          responseType: 'blob',
          data: data,
        })

        const blob = new Blob([response.data], { type: 'application/pdf' })
        const link = document.createElement('a')

        link.href = window.URL.createObjectURL(blob)
        link.setAttribute('download', 'prepaid_topup_resit.pdf')
        document.body.appendChild(link)
        link.click()
      } catch (e) {
        console.log(e)
      }
    },
  },
}
